import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useModal, useToast } from '@pcmp/react-providers';
import Button from '@ingka/button';
import urlIcon from '@ingka/ssr-icon/paths/link';
import textIcon from '@ingka/ssr-icon/paths/text-align-left';
import { Data } from '@pcmp/react-coworker-components';
import { usePostV2QrCodesMutation } from '@/redux/apis/codes-api';
import { useCtaReportPage } from '@/redux/features/cta/hooks';
import CodeSettings, { CodeSettingsField } from './code-settings';
import Location from './location';
import CommunicationCarrier from './communication-carrier';
import CommunicationPurpose from './communication-purpose';
import CreateCodeSteps from './create-codes-steps';
import { IFormInputs, generateSchema } from '@/lib/schemas/create-code-form';
import {
  serializeForm,
  serializeUser,
} from '@/lib/schemas/create-code-form/serialization';
import { useUser } from './context';
import useSchema from '@/lib/hooks/yup';
import { LoadingFullScreen } from '@/components/helpers/loading/index';
import { useLoading } from '@/components/helpers/loading/context';
import { useNavigate } from 'react-router-dom';
import CommunicationCarrierPrompt from '@/components/modals/communication-carrier-prompt';
import { COMMUNICATION_PURPOSES } from '@/lib/constants';
import CodePermissions from './permissions';

const { Card } = Data;

export const CreateCodeForm: FC = () => {
  const { watch, setValue } = useFormContext();
  const targetGroup = watch('targetGroup');
  const codeName = watch('codeName');
  const communicationPurpose = watch('communicationPurpose');
  const subcategory = watch('subcategory');
  const isTargetGroupCustomer = targetGroup === 'customer';
  const { t } = useTranslation();

  const codeSettingsFields: CodeSettingsField[] = [
    {
      name: 'targetUrl',
      label: t('label.URL'),
      type: 'text' as const,
      required: true,
      ssrIcon: urlIcon,
    },
    {
      name: 'codeName',
      label: t('label.codeName'),
      type: 'text' as const,
      required: true,
      ssrIcon: textIcon,
    },
  ];

  // If target group is customer, set default communicaiton purpose and subcategory
  useEffect(() => {
    if (isTargetGroupCustomer) {
      const defaultPurpose = COMMUNICATION_PURPOSES[0];
      const defaultSubcategory = defaultPurpose.subcategories?.[0];

      const shouldSetPurpose = !communicationPurpose;
      const shouldSetSubcategory = !subcategory;

      if (shouldSetPurpose) {
        setValue('communicationPurpose', defaultPurpose.value);
      }

      if (shouldSetSubcategory) {
        setValue('subcategory', defaultSubcategory?.value);
      }

      if (shouldSetPurpose && shouldSetSubcategory) {
        setValue('showIcon', true);
        setValue('icon', defaultSubcategory.icons[0]);
      }
    } else {
      setValue('showIcon', false);
    }
  }, [targetGroup]);

  if (!targetGroup && !codeName) return <CreateCodeSteps />;

  return (
    <>
      {isTargetGroupCustomer && (
        <CommunicationPurpose purposes={COMMUNICATION_PURPOSES} />
      )}
      <CodeSettings
        showIconSettings={true}
        purposes={COMMUNICATION_PURPOSES}
        fields={codeSettingsFields}
      />
      <Location />
      <CodePermissions />
      <CommunicationCarrier />
    </>
  );
};

export const CreateCodeButton: FC = () => {
  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  const triggerReportPage = useCtaReportPage();
  const [createQrCode] = usePostV2QrCodesMutation();
  const { pushToast } = useToast();
  const {
    getValues,
    formState: { isValid, isDirty },
    watch,
  } = useFormContext();
  const { user } = useUser();
  const { setLoading } = useLoading();
  const { openModal } = useModal();

  // Data
  const targetGroup = watch('targetGroup');

  // Event handlers
  const createCode = async () => {
    const data = getValues() as IFormInputs;

    const serializedData = {
      ...serializeForm(data, user),
      user: serializeUser(user),
    };

    setLoading(true);
    const response = await createQrCode({
      body: {
        ...serializedData,
      },
    });

    const isError = response && 'error' in response;
    const status = isError ? 'error' : 'success';
    const message = t(`page.createCode.messages.createCode.${status}`);

    // @ts-ignore
    pushToast({ message });

    if (!isError) {
      // Redirect to the newly created code
      navigate(`/codes/${response.data.slug}`);
      // CTA form: Report the page view
      triggerReportPage('createdCode');
    }
    setLoading(false);
  };

  const handleCreateCodeButton = async () => {
    const data = getValues() as IFormInputs;

    if (!data.carrier.size?.length || !data.carrier.type?.length) {
      openModal({
        component: CommunicationCarrierPrompt,
        props: {
          confirm: createCode,
        },
      });
    } else {
      createCode();
    }
  };

  if (!targetGroup) return null;

  return (
    <Button
      className="w-full lg:w-[327px]"
      iconPosition="leading"
      text={t('page.createCode.submit')}
      type="emphasised"
      disabled={!isValid || !isDirty}
      onClick={handleCreateCodeButton}
      data-testid="create-code-button"
    />
  );
};

export const CreateCodeFormWithSubmit: FC = () => {
  // Hooks
  const { user } = useUser();
  const { loading } = useLoading();

  // Form
  const resolver = useSchema(() => generateSchema());
  const form = useForm<IFormInputs>({
    mode: 'all',
    resolver,
    defaultValues: {
      onlyOnlineUsage: false,
      defaultLocation: true,
      location: {
        country: user?.country.toLowerCase(),
        site: user?.cdsLocation,
      },
      communicationPurpose: '',
      subcategory: '',
      permissions: 'private',
    },
    reValidateMode: 'onChange',
    criteriaMode: 'all',
  });

  if (loading) return <LoadingFullScreen textI18nKey="label.creatingCode" />;

  return (
    <FormProvider {...form}>
      <div className="w-full">
        <Card className="w-full mb-12">
          <CreateCodeForm />
        </Card>

        <div className="flex justify-end">
          <CreateCodeButton />
        </div>
      </div>
    </FormProvider>
  );
};
